<script>
import { mapGetters, mapActions } from 'vuex'
import { isCheckable } from 'src/utils/addressVerification'

/**
 * Google address verification state for one address, as an outline chip.
 * Informational only; nothing is held back because of it. Hidden while
 * address verification is turned off in Settings.
 *
 *   Verified    green   Google confirmed it as entered
 *   FIX         red     Google confirmed a corrected version; click to compare and accept
 *                       (or, for a result loaded from the server, to fetch the suggestion)
 *   Unverified  red     Google could not confirm it; click for what to do
 *   Retry       orange  Google could not be reached; click to try again
 *   Unverified  grey    not checked yet; click to check now
 *
 * The parent runs the checks (on blur, on save) through the
 * addressVerification store; this only reads the result for its address.
 * Accepting a correction emits `accept` with Google's version, which the
 * parent writes into its fields.
 */
export default {
  name: 'AddressVerifyChip',
  props: {
    address: { type: Object, required: true },
    // The saved address row, so a check of an unedited address is stored
    addressId: { type: Number, default: null },
    disable: Boolean,
  },
  computed: {
    ...mapGetters(['stateOptions']),
    ...mapGetters('addressVerification', ['checkFor']),
    ...mapGetters('settings', ['addressVerificationEnabled']),
    check () {
      return this.checkFor(this.address)
    },
    status () {
      if (!this.addressVerificationEnabled) return null
      if (!isCheckable(this.address)) return null
      return this.check ? this.check.status : 'unchecked'
    },
    chip () {
      switch (this.status) {
        case 'checking': return { label: 'Checking', color: 'grey', icon: 'autorenew', spin: true }
        case 'verified': return { label: 'Verified', color: 'positive', icon: 'check_circle' }
        case 'fix': return { label: 'FIX', color: 'negative', icon: 'build', title: 'Google suggests a correction. Click to review it.' }
        case 'unverified': return { label: 'Unverified', color: 'negative', icon: 'error_outline', title: 'Google could not confirm this address. Click for details.' }
        case 'unavailable': return { label: 'Retry', color: 'warning', icon: 'cloud_off', title: 'Address verification could not be reached. Click to try again.' }
        case 'unchecked': return { label: 'Unverified', color: 'grey', icon: 'help_outline', title: 'Not checked yet. Click to check now.' }
        default: return null
      }
    },
    hasSuggestion () {
      return !!(this.check && this.check.suggestion)
    },
    // A `fix` loaded from the server has no suggestion yet; clicking asks
    // Google for it instead of opening an empty comparison.
    needsCheck () {
      return this.status === 'unavailable' || this.status === 'unchecked'
        || (this.status === 'fix' && !this.hasSuggestion)
    },
    hasPopover () {
      return (this.status === 'fix' && this.hasSuggestion) || this.status === 'unverified'
    },
    clickable () {
      return !this.disable && (this.hasPopover || this.needsCheck)
    },
    enteredText () {
      return this.formatAddress(this.address)
    },
    suggestedText () {
      return this.check && this.check.suggestion ? this.formatAddress(this.check.suggestion) : ''
    },
  },
  methods: {
    ...mapActions('addressVerification', { runCheck: 'check' }),
    formatAddress ({ address1, address2, city, stateId, zip } = {}) {
      const state = this._.find(this.stateOptions, { value: stateId })
      const stateZip = [state ? state.label : '', zip].filter(Boolean).join(' ')
      const cityLine = [city, stateZip].filter(Boolean).join(', ')
      return [[address1, address2].filter(Boolean).join(' '), cityLine].filter(Boolean).join('\n')
    },
    onClick () {
      if (this.disable) return
      if (this.needsCheck) {
        this.runCheck({ address: this.address, addressId: this.addressId, force: true })
      }
    },
    accept () {
      const suggestion = this.check && this.check.suggestion
      if (this.$refs.popover) this.$refs.popover.hide()
      if (suggestion) this.$emit('accept', suggestion)
    },
    checkAgain () {
      if (this.$refs.popover) this.$refs.popover.hide()
      this.runCheck({ address: this.address, addressId: this.addressId, force: true })
    },
    closePopover () {
      if (this.$refs.popover) this.$refs.popover.hide()
    },
  },
}
</script>

<template>
  <span
    v-if="chip"
    class="address-verify-chip"
    :class="[`text-${chip.color}`, { clickable }]"
    :title="chip.title"
    @click="onClick"
  >
    <q-icon :name="chip.icon" :class="{ spin: chip.spin }" />
    <span>{{ chip.label }}</span>

    <q-popover
      v-if="hasPopover && !disable"
      ref="popover"
      anchor="bottom left"
      self="top left"
      :offset="[0, 4]"
    >
      <div class="address-verify-popover">
        <template v-if="status === 'fix'">
          <div class="caption">You entered</div>
          <div class="address entered">{{ enteredText }}</div>
          <div class="caption">Google suggests</div>
          <div class="address suggested">{{ suggestedText }}</div>
          <div class="actions">
            <q-btn flat dense size="sm" color="grey-8" label="Cancel" @click="closePopover" />
            <q-btn dense size="sm" color="positive" label="Accept" class="q-px-sm" @click="accept" />
          </div>
        </template>
        <template v-else>
          <div class="message">
            Google could not confirm this address. The class can still be saved; if the address is wrong, check the street, unit, city, state and ZIP, then try again.
          </div>
          <div class="address entered">{{ enteredText }}</div>
          <div class="actions">
            <q-btn flat dense size="sm" color="grey-8" label="Close" @click="closePopover" />
            <q-btn dense size="sm" color="primary" label="Check again" class="q-px-sm" @click="checkAgain" />
          </div>
        </template>
      </div>
    </q-popover>
  </span>
</template>

<style lang="stylus">
.address-verify-chip
  display inline-flex
  align-items center
  flex 0 0 auto
  gap 3px
  height 22px
  padding 0 8px
  border 1px solid currentColor
  border-radius 11px
  background transparent
  font-size 11px
  font-weight 600
  letter-spacing .3px
  line-height 1
  white-space nowrap
  user-select none

  .q-icon
    font-size 13px

  &.clickable
    cursor pointer

    &:hover
      background rgba(0, 0, 0, .05)

  .spin
    animation address-verify-spin 1s linear infinite

@keyframes address-verify-spin
  from
    transform rotate(0deg)
  to
    transform rotate(360deg)

.address-verify-popover
  width 280px
  padding 12px

  .caption
    font-size 11px
    font-weight 600
    letter-spacing .5px
    text-transform uppercase
    color #757575
    margin 0 0 4px

  .message
    font-size 13px
    margin 0 0 8px

  .address
    white-space pre-line
    font-size 13px
    padding 6px 8px
    border-radius 4px
    margin 0 0 10px

    &.entered
      background #f5f5f5

    &.suggested
      background #e8f5e9

  .actions
    display flex
    justify-content flex-end
    gap 6px
</style>
